import React from "react";
import PropTypes from "prop-types";

import NoDataPreview from "@/components/shared/noDataPreview";
import { fetchInternalFairStaffingRows } from "./utils";

import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Paper,
    makeStyles,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";

const useStyles = makeStyles((theme) => ({
    actionsRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "stretch",
        },
    },
    searchField: {
        minWidth: 320,
        maxWidth: 520,
        background: theme.palette.common.white,
        [theme.breakpoints.down("sm")]: {
            minWidth: 0,
            maxWidth: "100%",
            width: "100%",
        },
    },
    tableCell: {
        verticalAlign: "top",
        wordBreak: "break-word",
        minWidth: 120,
    },
    tableHeadCell: {
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
}));

const columns = [
    "Total Rooms",
    "University Name",
    "Country",
    "Desk Number",
    "Staff",
    "Connect Links",
    "CRM ID",
];

const normalizeSearchValue = (value) => String(value || "").trim().toLowerCase();

export default function FinalStaffingTab({ jsonUrl }) {
    const classes = useStyles();
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");
    const [searchTerm, setSearchTerm] = React.useState("");
    const [rows, setRows] = React.useState([]);

    const loadRows = React.useCallback(() => {
        setLoading(true);
        setError("");

        return fetchInternalFairStaffingRows({ jsonUrl })
            .then((data) => setRows(data))
            .catch(() => {
                setRows([]);
                setError("Unable to load final staffing data right now.");
            })
            .finally(() => setLoading(false));
    }, [jsonUrl]);

    React.useEffect(() => {
        loadRows();
    }, [loadRows]);

    const filteredRows = React.useMemo(() => {
        const normalizedSearchTerm = normalizeSearchValue(searchTerm);
        if (!normalizedSearchTerm) return rows;

        return rows.filter((row) =>
            columns.some((column) => normalizeSearchValue(row[column]).includes(normalizedSearchTerm))
        );
    }, [rows, searchTerm]);

    const renderCellValue = (column, value) => {
        if (!value) return "-";
        if (column === "Connect Links" && String(value).startsWith("http")) {
            return (
                <Link href={value} target="_blank" rel="noopener noreferrer">
                    Open link
                </Link>
            );
        }

        return String(value);
    };

    if (loading) {
        return (
            <Box textAlign="center" py={4}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    if (!loading && rows.length === 0) {
        return <NoDataPreview title={error || "No staffing data found"} />;
    }

    return (
        <>
            <Box className={classes.actionsRow}>
                <TextField
                    className={classes.searchField}
                    variant="outlined"
                    label="Search Final Staffing"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search by university, staff, country, desk..."
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button variant="outlined" color="primary" onClick={loadRows} startIcon={<RefreshIcon />}>
                    Refresh Final Staffing
                </Button>
            </Box>

            {error ? (
                <Typography variant="body2" color="error" gutterBottom>
                    {error}
                </Typography>
            ) : null}

            {filteredRows.length === 0 ? (
                <NoDataPreview title="No staffing rows found for the current search" />
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell key={column} className={`${classes.tableCell} ${classes.tableHeadCell}`}>
                                        {column}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredRows.map((row, index) => (
                                <TableRow key={`${row["University Name"] || "row"}-${row["Total Rooms"] || index}-${index}`}>
                                    {columns.map((column) => (
                                        <TableCell key={column} className={classes.tableCell}>
                                            {renderCellValue(column, row[column])}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    );
}

FinalStaffingTab.propTypes = {
    jsonUrl: PropTypes.string,
};
